// ─────────────────────────────────────────────────────────────────────────────
// AppShell — authenticated-tree wrapper for Chunk 6.
//
// SINGLE OWNER (Ghost Flag 3 + FIX 1) of the Chunk-5 lifecycle that was
// previously scattered in RexyHomepage: useRexySocket, useMicMeter,
// useSpacebarPTT, signal RTT probe, telemetry-feed accumulator, warmth, and
// `active` (PTT). Exposes the values to its subtree via <AppShellLiveContext>
// and the useAppShellLive() hook (see AppShellContext.tsx). App.tsx never reads
// them — they live entirely inside this subtree.
//
// Renders the persistent chrome:
//   • <AtmosphereBackground/>      — once, lifted from RexyHomepage (Flag 4)
//   • <BackChevron/>               — top-left, hidden on home
//   • <CornerOrb/>                 — top-right, hidden on home (Q1)
//   • {children}                   — the active screen, supplied by App.tsx
//   • <DomainBar position="bottom" homePill .../> — bottom (CORRECTION 1
//                                    maps onDomainPress → onDomainChange)
//
// Built but UNMOUNTED from App.tsx this check-in (per CORRECTION 2). The
// RexyHomepage migration to useAppShellLive() happens atomically with the
// App.tsx mount in Check-in 4.
//
// PIN screen never sees this shell (Flag 4) — App.tsx renders RexyPinScreen
// outside AppShell when !isAuthenticated.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import AtmosphereBackground from '../AtmosphereBackground';
import DomainBar from '../DomainBar';
import { useRexySocket } from '../../hooks/useRexySocket';
import { useVoiceDaemonPTT } from '../../hooks/useVoiceDaemonPTT';
import { TelemetryFeedEntry } from '../TelemetryCard';
import { AppShellLiveContext, AppShellLiveValue } from './AppShellContext';
import CornerOrb from './CornerOrb';
import BackChevron from './BackChevron';

const TELEMETRY_CAP = 30;

export interface AppShellProps {
  // Routing (driven by App.tsx state — passed in from above).
  screen: 'home' | 'domain' | 'agent';
  legacyDashboardOpen: boolean; // showCodeDashboard || showSecurityDashboard || showArthurDashboard
  activeDomain: string;
  activeAgent: string | null;

  // Navigation callbacks (UI state only — Flag 10).
  onHomePress: () => void; // sets screen='home'; NO backend POST (Flag 2)
  onDomainPress: (domain: string) => void; // App.tsx composes handleDomainChange + setScreen('domain')
  onBackPress: () => void; // steps screen; preserves activeDomain/activeAgent (Flag 10)

  // SETTINGS — a page on the bar, not a domain. Passed straight through to
  // DomainBar; AppShell does not branch on it, and activeDomain is untouched.
  settingsActive?: boolean;
  onSettingsPress?: () => void;

  children: React.ReactNode;
}

export default function AppShell({
  screen,
  legacyDashboardOpen,
  activeDomain,
  activeAgent,
  onHomePress,
  onDomainPress,
  onBackPress,
  settingsActive,
  onSettingsPress,
  children,
}: AppShellProps) {
  // ── Lifted lifecycle (single owner per Flag 3) ─────────────────────────────
  const { connected, vitals, telemetry: latestTel } = useRexySocket();

  // 5.PTT — the homepage PTT session is REXY's front door: bind {HOMEPAGE, REXY}
  // atomically (one pair, never desynced — project_domain_agent_pair_drift) so a
  // homepage tap reaches the persona-loaded Rexy in her Aurora voice. Off the
  // homepage the session keeps the active {domain, agent}. PTT mechanism untouched.
  const isOnHomepage = screen === 'home' && !legacyDashboardOpen;
  const ptt = useVoiceDaemonPTT(
    isOnHomepage
      ? { activeDomain: 'HOMEPAGE', activeAgent: 'REXY' }
      : { activeDomain, activeAgent },
  );
  const pttAction = ptt.ptt;
  const active = ptt.state !== 'idle' && ptt.state !== 'ending' && ptt.state !== 'error';

  // Spacebar shortcut removed (Chunk 7 user decision): PTT is driven by the
  // dome mouse-tap only. Keyboard PTT can be re-added in a later chunk if
  // needed without affecting the rest of the pipeline.

  // Warmth target — locked VoiceState→warmth map (iOS parity, Phase 1.2). Single
  // shared source feeding BOTH the orb and the sibling atmosphere; each consumer
  // eases toward it in its own render path (orb rAF exp-lerp, atmosphere CSS), so
  // the shell never re-renders at 60fps for a purely visual concern. ptt.state and
  // ptt.userAmplitude are already provider deps — no new render cadence introduced.
  const warmth =
    ptt.state === 'thinking' || ptt.state === 'agent_speaking'
      ? 1.0
      : ptt.state === 'user_speaking'
      ? ptt.userAmplitude * 0.3
      : 0.0; // idle / connecting / listening / ending / error

  // The signal RTT probe is REMOVED (owner ruling 2026-07-24). It measured this
  // client's own browser->Flask round-trip and saturated at 100% under 50ms — a fake
  // constant, and per-window rather than per-core. SIGNAL is merged into the
  // server-sampled `connection_ms` on rexy:vitals, so every window shows the SAME
  // DGX connection figure. No ping interval, no rexy:pong listener.

  // Telemetry rolling 30-entry accumulator with stable ids — lifted verbatim.
  const [feed, setFeed] = useState<TelemetryFeedEntry[]>([]);
  const seqRef = useRef(0);
  useEffect(() => {
    if (!latestTel) return;
    const entry: TelemetryFeedEntry = { ...latestTel, id: `tel_${seqRef.current++}` };
    setFeed((prev) => [entry, ...prev].slice(0, TELEMETRY_CAP));
  }, [latestTel]);

  // ── Provider value ─────────────────────────────────────────────────────────
  const liveValue: AppShellLiveValue = useMemo(
    () => ({
      connected,
      vitals,
      feed,
      meter: ptt.meter,
      warmth,
      active,
      state: ptt.state,
      userAmplitude: ptt.userAmplitude,
      agentAmplitude: ptt.agentAmplitude,
      ptt: pttAction,
      error: ptt.error,
      muted: ptt.muted,
      mutedStandingS: ptt.mutedStandingS ?? 0,
      toggleMute: ptt.toggleMute,
      partialTranscript: ptt.partialTranscript,
      agentText: ptt.agentText,
      // The per-agent call surface — owner-approved context widening, so a
      // button on an agent's page reaches THE SINGLE daemon client rather than
      // constructing a second one.
      start: ptt.start ?? null,
      boxCall: ptt.boxCall ?? null,
      adopted: ptt.adopted ?? null,
      daemonBinding: ptt.daemonBinding ?? null,
      // RAW, not the homepage substitution above: `useVoiceDaemonPTT` is handed
      // HOMEPAGE/REXY on the homepage so the sphere reaches Rexy, but a control
      // on an agent's page must read where the owner actually is.
      navDomain: activeDomain,
      navAgent: activeAgent,
    }),
    [
      connected,
      vitals,
      feed,
      ptt.meter,
      warmth,
      active,
      ptt.state,
      ptt.userAmplitude,
      ptt.agentAmplitude,
      pttAction,
      ptt.error,
      ptt.muted,
      ptt.mutedStandingS,
      ptt.toggleMute,
      ptt.partialTranscript,
      ptt.agentText,
      ptt.start,
      ptt.boxCall,
      ptt.adopted,
      ptt.daemonBinding,
      activeDomain,
      activeAgent,
    ],
  );

  return (
    <div style={rootStyle}>
      <AppShellLiveContext.Provider value={liveValue}>
        <AtmosphereBackground warmth={warmth} />

        {!isOnHomepage && <BackChevron onPress={onBackPress} />}
        {!isOnHomepage && <CornerOrb />}

        <div style={contentStyle}>{children}</div>

        <div style={domainBarSlotStyle}>
          {/* CORRECTION 1: map AppShell's onDomainPress → DomainBar's existing onDomainChange. */}
          <DomainBar
            position="bottom"
            homePill
            activeDomain={activeDomain}
            onHomePress={onHomePress}
            onHome={isOnHomepage}
            onDomainChange={onDomainPress}
            settingsActive={settingsActive}
            onSettingsPress={onSettingsPress}
          />
        </div>
      </AppShellLiveContext.Provider>
    </div>
  );
}

const rootStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const contentStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  position: 'relative',
  zIndex: 1,
};

const domainBarSlotStyle: React.CSSProperties = {
  position: 'relative',
  zIndex: 1,
  flexShrink: 0,
};
